import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

export default function useCollapsible({
  defaultOpen = false,
  duration = 200,
  open: controlledOpen,
} = {}) {
  const contentRef = useRef(null);
  const [internalOpen, setInternalOpen] = useState(!!defaultOpen);

  const isControlled = typeof controlledOpen === 'boolean';
  const open = isControlled ? controlledOpen : internalOpen;

  const [maxHeight, setMaxHeight] = useState(0);

  const recompute = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    if (open) setMaxHeight(el.scrollHeight);
  }, [open]);

  useLayoutEffect(() => {
    recompute();
  }, [recompute, open]);

  useEffect(() => {
    const el = contentRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    const ro = new ResizeObserver(() => {
      if (open) setMaxHeight(el.scrollHeight);
    });

    ro.observe(el);
    // eslint-disable-next-line consistent-return
    return () => ro.disconnect();
  }, [open]);

  const toggle = useCallback(() => {
    if (!isControlled) {
      setInternalOpen((v) => !v);
    }
  }, [isControlled]);

  const contentStyle = useMemo(
    () => ({
      maxHeight: open ? maxHeight : 0,
      overflow: 'hidden',
      transition: `max-height ${duration}ms ease, opacity ${duration}ms ease`,
      opacity: open ? 1 : 0,
      pointerEvents: open ? 'auto' : 'none',
    }),
    [open, maxHeight, duration],
  );

  return { contentRef, open, toggle, setOpen: setInternalOpen, contentStyle };
}
